import { OTHER_PLAYER_RUNESTONE_CABINET_QUERY } from "@/graphql/queries/profileQueries";
import { classNamesExtended } from "@/helpers/common/classNames";
import { formatNumber } from "@/helpers/common/formatNumber";
import { convertLetters } from "@/utils/letterConversion";
import { useQuery } from "@apollo/client";
import { useState } from "react";

interface RunestoneCabinetEntry {
    hasRedeemed: boolean;
    redemptionCount: number;
    ravenguardPoints: number;
    cooldownExpiresAt?: string | null;
    cooldownRemainingSeconds: number;
    streamerUrl?: string | null;
    streamTeamMember: {
        id: string;
        twitchUsername?: string | null;
        player: {
            id: string;
            name: string;
        };
    };
    runestone: {
        id: string;
        runeName: string;
        imageUrl: string | null;
    } | null;
}

interface OtherPlayerRunestoneCabinetQueryData {
    otherPlayerRunestoneCabinet: RunestoneCabinetEntry[];
}

interface OtherPlayerRunestonesCabinetProps {
    playerId: string;
}

interface RunestoneCardProps {
    entry: RunestoneCabinetEntry;
}

function formatCooldown(seconds: number): string {
    if (seconds <= 0) {
        return "Ready";
    }

    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    return `${days}d ${hours}h ${minutes}m`;
}

function getRavenguardBaseUrl(): string {
    const hostname = window.location.hostname.toLowerCase();
    const isLocalEnv =
        hostname === "localhost" ||
        hostname === "127.0.0.1" ||
        hostname.endsWith(".local");

    if (isLocalEnv) {
        return "http://ravenguard.vanderheim.local:5176";
    }

    return "https://ravenguard.vanderheim.com";
}

function getStreamerProfileUrl(entry: RunestoneCabinetEntry): string | null {
    const username = entry.streamTeamMember.twitchUsername?.trim();
    if (!username) {
        return entry.streamerUrl ?? null;
    }

    return `${getRavenguardBaseUrl()}/team/${encodeURIComponent(username)}`;
}

function RunestoneCard({ entry }: RunestoneCardProps) {
    const [hovered, setHovered] = useState(false);
    const flipped = hovered;

    const displayName = entry.streamTeamMember.player.name;
    const imageUrl = entry.runestone?.imageUrl;

    const CardContent = (
        <div
            className={classNamesExtended(
                "border-brand relative aspect-square w-full max-w-32.5 cursor-pointer overflow-hidden rounded border-4 sm:max-w-none",
                {
                    "border-gray-300": !entry.hasRedeemed,
                },
            )}
            style={{ perspective: "1000px" }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            title={`${displayName} (${entry.hasRedeemed ? "Redeemed" : "Not redeemed"})`}
        >
            <div
                className="relative h-full w-full transform transition-transform duration-700"
                style={{
                    transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
                    transformStyle: "preserve-3d",
                }}
            >
                <div
                    className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/20 p-2"
                    style={{ backfaceVisibility: "hidden" }}
                >
                    {imageUrl ? (
                        <img
                            src={imageUrl}
                            alt={`${displayName} Runestone`}
                            className={classNamesExtended(
                                "max-h-[70%] max-w-full object-contain",
                                {
                                    grayscale: !entry.hasRedeemed,
                                },
                            )}
                        />
                    ) : (
                        <div className="flex h-[70%] w-full items-center justify-center rounded bg-slate-800/60 px-2 text-center text-xs text-slate-300">
                            No Runestone
                        </div>
                    )}
                    <p className="text-center text-xs font-semibold text-slate-100 sm:text-sm">
                        {convertLetters(displayName)}
                    </p>
                </div>

                <div
                    className="bg-brand_charcoal text-brand absolute inset-0 flex flex-col items-center justify-center rounded p-2 text-xs sm:text-sm"
                    style={{
                        backfaceVisibility: "hidden",
                        transform: "rotateY(180deg)",
                    }}
                >
                    <h3 className="font-prviking mb-2 text-center text-base font-bold sm:text-lg">
                        {convertLetters(displayName)}
                    </h3>
                    <div className="space-y-1 text-left">
                        <p>
                            <strong>STATUS:</strong>{" "}
                            <span className="text-stats">
                                {entry.hasRedeemed
                                    ? "Redeemed"
                                    : "Not Redeemed"}
                            </span>
                        </p>
                        <p>
                            <strong>RP:</strong>{" "}
                            <span className="text-stats">
                                {formatNumber(entry.ravenguardPoints || 0)}
                            </span>
                        </p>
                        <p>
                            <strong>REDEMPTIONS:</strong>{" "}
                            <span className="text-stats">
                                {formatNumber(entry.redemptionCount || 0)}
                            </span>
                        </p>
                        <p>
                            <strong>COOLDOWN:</strong>{" "}
                            <span className="text-stats">
                                {formatCooldown(
                                    entry.cooldownRemainingSeconds || 0,
                                )}
                            </span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const streamerProfileUrl = getStreamerProfileUrl(entry);

    if (!streamerProfileUrl) {
        return CardContent;
    }

    return (
        <a href={streamerProfileUrl} className="block">
            {CardContent}
        </a>
    );
}

export default function OtherPlayerRunestonesCabinet({
    playerId,
}: OtherPlayerRunestonesCabinetProps) {
    const { data, loading, error } =
        useQuery<OtherPlayerRunestoneCabinetQueryData>(
            OTHER_PLAYER_RUNESTONE_CABINET_QUERY,
            {
                variables: { playerId },
                skip: !playerId,
                fetchPolicy: "cache-and-network",
            },
        );

    if (loading && !data) {
        return <p>Loading...</p>;
    }

    if (error && data) {
        console.warn(
            "Other player runestones refresh failed, showing cached data.",
        );
    }

    const entries = data?.otherPlayerRunestoneCabinet ?? [];

    return (
        <div className="w-full">
            <div className="grid grid-cols-2 justify-center gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                {entries.map((entry) => (
                    <RunestoneCard
                        key={entry.streamTeamMember.id}
                        entry={entry}
                    />
                ))}
            </div>
        </div>
    );
}
